import { useMemo } from "react"
import { createSearchParams, Link, useLocation } from "react-router-dom"
import { observer } from "mobx-react"
import { Menu } from "antd"
import { DashboardOutlined } from "@ant-design/icons"

import sprite from "@src/app/assets/navigation/sprite.svg"
import { authStore } from "@src/app/authStore"
import { paths } from "@src/app/paths"
import uiStore from "@src/app/uiStore"
import { MassPayoutPageVariants } from "@src/pages/massPayouts/constants"
import { MassPayoutsPageView } from "@src/pages/massPayouts/crypto"
import css from "./style.module.css"

const Icon = ({ name, size = 18 }: { name: string; size?: number }) => {
  const path = `${sprite}#${name}`

  return (
    <svg height={size} width={size}>
      <use xlinkHref={path} />
    </svg>
  )
}

type AsideMenuProps = {
  isHorizontal?: boolean
}

const AsideMenu = observer(({ isHorizontal = false }: AsideMenuProps) => {
  const { pathname } = useLocation()
  const { isTransferioTheme } = uiStore()

  const m = useMemo(() => {
    return [
      {
        className: css.item,
        icon: <DashboardOutlined style={{ fontSize: 16 }} />,
        key: paths.analytics,
        label: (
          <Link
            id="GENERATED_ID_ASIDEMENU_LINK_ANALYTICS_1"
            to={paths.analytics}
          >
            Analytics
          </Link>
        ),
      },
      {
        icon: <Icon name="balances" />,
        key: paths.balances,
        label: (
          <Link id="GENERATED_ID_ASIDEMENU_LINK_BALANCES_1" to={paths.balances}>
            Balances
          </Link>
        ),
      },
      {
        icon: <Icon name="projects" />,
        key: paths.projects,
        label: (
          <Link
            id="GENERATED_ID_ASIDEMENU_LINK_MY_PROJECTS_1"
            to={paths.projects}
          >
            Projects
          </Link>
        ),
      },
      {
        icon: <Icon name="transactions" />,
        key: paths.transactions,
        label: (
          <Link
            id="GENERATED_ID_ASIDEMENU_LINK_TRANSACTIONS_1"
            to={paths.transactions}
          >
            Transactions
          </Link>
        ),
      },
      {
        icon: <Icon name="swap" />,
        isVisible: !isTransferioTheme,
        key: paths.swap,
        label: (
          <Link id="GENERATED_ID_ASIDEMENU_LINK_SWAP_1" to={paths.swap}>
            Swap
          </Link>
        ),
      },
      {
        icon: <Icon name="reports" />,
        key: paths.reports,
        label: (
          <Link id="GENERATED_ID_ASIDEMENU_LINK_REPORTS_1" to={paths.reports}>
            Reports
          </Link>
        ),
      },
      {
        icon: <Icon name="checkouts" />,
        key: paths.checkouts,
        label: (
          <Link
            id="GENERATED_ID_ASIDEMENU_LINK_CHECKOUTS_1"
            to={paths.checkouts}
          >
            Checkouts
          </Link>
        ),
      },
      {
        icon: <Icon name="payments-links" />,
        key: paths.paymentLinks,
        label: (
          <Link
            id="GENERATED_ID_ASIDEMENU_LINK_PAYMENT_LINKS_1"
            to={paths.paymentLinks}
          >
            Payment Links
          </Link>
        ),
      },
      {
        icon: <Icon name="mass-payouts" />,
        key: paths.massPayouts,
        label: (
          <Link
            id="GENERATED_ID_ASIDEMENU_LINK_MASS_PAYOUTS_1"
            to={{
              pathname: paths.massPayouts,
              search: `?${createSearchParams({ view: String(MassPayoutPageVariants.Crypto) })}`,
            }}
          >
            Mass Payouts
          </Link>
        ),
      },
      {
        icon: <Icon name="settings" />,
        key: paths.settings,
        label: (
          <Link id="GENERATED_ID_ASIDEMENU_LINK_SETTINGS_1" to={paths.settings}>
            Settings
          </Link>
        ),
      },
    ]
      .filter(({ isVisible }) => (isVisible !== undefined ? isVisible : true))
      .map(({ isVisible: _, ...rest }) => ({ ...rest }))
  }, [authStore.merchantInfo])

  return (
    <Menu
      className={css.menu}
      inlineCollapsed={isHorizontal ? undefined : false}
      items={m}
      mode={isHorizontal ? "horizontal" : "vertical"}
      selectedKeys={[pathname]}
    />
  )
})

export default AsideMenu
